// Session 147j+16 — OI-VWAP Confluence panel.
//
// Strategy (per user playbook):
//   Filter:        Long → Price > VWAP · Short → Price < VWAP
//   OI Magnitude:  oi > oi[15] * 1.02            → is_significant
//   OI Quality:    linreg(oi, 15) slope > 0      → is_trending
//   Vol Surge:     volume > sma(vol, 20) * 1.5   → vol_spike
//   VWAP Pullback: close > vwap AND close < vwap*1.001 AND close > open  → at_value
//   VWAP Slope:    vwap > vwap[5]                → vwap_up
//   LONG = all 5 align bullish · SHORT = mirror
//
// Plus the 4-row Interpretation Matrix sentiment label.
//
// Backend: /api/admin/oi-vwap/{status,signals,config,run-now,backfill}

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Activity, ArrowUpRight, ArrowDownRight, Settings, RefreshCw, Target,
  TrendingUp, TrendingDown, Zap, AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'token';
const authHeaders = () => {
  const t = localStorage.getItem(TOKEN_KEY);
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const SENTIMENT_TONE = {
  STRONG_BULLISH: 'border-emerald-500/60 bg-emerald-500/15 text-emerald-200',
  STRONG_BEARISH: 'border-rose-500/60 bg-rose-500/15 text-rose-200',
  WEAK_RALLY_BEARISH: 'border-amber-500/40 bg-amber-500/10 text-amber-200',
  WEAK_SELLOFF_BULLISH: 'border-sky-500/40 bg-sky-500/10 text-sky-200',
  NEUTRAL: 'border-border bg-surface-highlight text-muted-foreground',
};

const SIGNAL_TONE = {
  LONG: 'border-emerald-500/60 bg-emerald-500/20 text-emerald-100',
  SHORT: 'border-rose-500/60 bg-rose-500/20 text-rose-100',
  NONE: 'border-border bg-surface-highlight text-muted-foreground',
};

const fmt = (v, d = 2) => (v == null ? '—' : Number(v).toFixed(d));
const fmtOI = (v) => {
  if (v == null) return '—';
  if (v >= 1e7) return (v / 1e7).toFixed(2) + ' Cr';
  if (v >= 1e5) return (v / 1e5).toFixed(2) + ' L';
  if (v >= 1e3) return (v / 1e3).toFixed(1) + ' K';
  return String(v);
};

// ---------------------------------------------------------------------
// Live banner — current price/VWAP, OI Δ%, vol×, sentiment
// ---------------------------------------------------------------------
const LiveBanner = ({ snap, status }) => {
  if (!snap) {
    const msg = status?.market_open
      ? 'Loop is running but no snapshot persisted yet — wait ~60s.'
      : status?.market_open === false
      ? 'Market closed — loop will resume at 09:15 IST. Use Backfill 1d to seed history, or Run now to fetch the latest bars manually.'
      : 'No live snapshot yet — click Run now.';
    return (
      <div className="text-xs text-muted-foreground" data-testid="oi-vwap-banner-loading">
        {msg}
      </div>
    );
  }
  const oiDeltaPct = snap.oi_lookback && snap.oi
    ? ((snap.oi - snap.oi_lookback) / snap.oi_lookback) * 100
    : null;
  const volMult = snap.vol_sma && snap.vol_sma > 0
    ? snap.volume / snap.vol_sma
    : null;
  const distVwap = snap.vwap && snap.close
    ? ((snap.close - snap.vwap) / snap.vwap) * 100
    : null;
  return (
    <div
      className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 border border-border bg-surface p-3"
      data-testid="oi-vwap-live-banner"
    >
      <div>
        <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Price · VWAP</div>
        <div className="text-sm font-mono text-foreground">
          {fmt(snap.close, 1)}{' '}
          <span className={distVwap == null ? 'text-muted-foreground'
            : distVwap >= 0 ? 'text-emerald-300' : 'text-rose-300'}>
            ({distVwap == null ? '—' : (distVwap >= 0 ? '+' : '') + distVwap.toFixed(2) + '%'})
          </span>
        </div>
        <div className="text-[10px] font-mono text-muted-foreground">vwap {fmt(snap.vwap, 1)}</div>
      </div>
      <div>
        <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">15-min OI Δ</div>
        <div className={`text-sm font-mono ${oiDeltaPct == null ? 'text-muted-foreground'
          : oiDeltaPct >= 2 ? 'text-emerald-300' : 'text-foreground'}`}>
          {oiDeltaPct == null ? '—' : (oiDeltaPct >= 0 ? '+' : '') + oiDeltaPct.toFixed(2) + '%'}
        </div>
        <div className="text-[10px] font-mono text-muted-foreground">oi {fmtOI(snap.oi)}</div>
      </div>
      <div>
        <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Volume × SMA</div>
        <div className={`text-sm font-mono ${volMult == null ? 'text-muted-foreground'
          : volMult >= 1.5 ? 'text-emerald-300' : 'text-foreground'}`}>
          {volMult == null ? '—' : volMult.toFixed(2) + '×'}
        </div>
        <div className="text-[10px] font-mono text-muted-foreground">sma {fmt(snap.vol_sma, 0)}</div>
      </div>
      <div>
        <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">OI Slope · R²</div>
        <div className="text-sm font-mono text-foreground">
          {fmt(snap.oi_slope, 1)}
        </div>
        <div className={`text-[10px] font-mono ${snap.oi_r2 != null && snap.oi_r2 >= 0.5
          ? 'text-emerald-300' : 'text-muted-foreground'}`}>
          R² {fmt(snap.oi_r2, 2)}
        </div>
      </div>
      <div>
        <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Sentiment</div>
        <span
          className={`inline-block px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider border ${
            SENTIMENT_TONE[snap.sentiment] || SENTIMENT_TONE.NEUTRAL
          }`}
          data-testid="oi-vwap-sentiment"
        >
          {(snap.sentiment || 'NEUTRAL').replace(/_/g, ' ').toLowerCase()}
        </span>
      </div>
      <div>
        <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Signal</div>
        <span
          className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-mono uppercase tracking-wider border ${
            SIGNAL_TONE[snap.signal] || SIGNAL_TONE.NONE
          }`}
          data-testid="oi-vwap-signal"
        >
          {snap.signal === 'LONG' && <ArrowUpRight className="w-3 h-3" />}
          {snap.signal === 'SHORT' && <ArrowDownRight className="w-3 h-3" />}
          {snap.signal}
        </span>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------
// Boolean panel — green/red dots for the 5 conditions
// ---------------------------------------------------------------------
const Dot = ({ on }) => (
  <span
    className={`inline-block w-2 h-2 rounded-full ${
      on ? 'bg-emerald-400' : 'bg-rose-400/50'
    }`}
  />
);

const ConditionsRow = ({ snap }) => {
  if (!snap) return null;
  const items = [
    { k: 'is_significant', label: 'OI ≥ +2% (15m)' },
    { k: 'is_trending', label: 'OI linreg slope > 0' },
    { k: 'vol_spike', label: 'Vol > SMA × 1.5' },
    { k: 'at_value_long', label: 'Long pullback' },
    { k: 'at_value_short', label: 'Short rejection' },
    { k: 'vwap_up', label: 'VWAP up' },
    { k: 'vwap_down', label: 'VWAP down' },
  ];
  return (
    <div
      className="border border-border bg-surface p-3 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2"
      data-testid="oi-vwap-conditions"
    >
      {items.map((it) => (
        <div key={it.k} className="flex items-center gap-1.5">
          <Dot on={!!snap[it.k]} />
          <span className="text-[10px] font-mono text-muted-foreground">{it.label}</span>
        </div>
      ))}
    </div>
  );
};

// ---------------------------------------------------------------------
// Tunable threshold card
// ---------------------------------------------------------------------
const ConfigCard = ({ symbol, config, onSave }) => {
  const [edit, setEdit] = useState(null);
  useEffect(() => { setEdit(config); }, [config]);
  if (!edit) return null;
  const dirty = JSON.stringify(edit) !== JSON.stringify(config);
  const num = (k) => (e) => setEdit({ ...edit, [k]: Number(e.target.value) });
  const fields = [
    { k: 'oi_lookback', label: 'OI lookback (bars)', step: 1 },
    { k: 'oi_magnitude_pct', label: 'OI magnitude %', step: 0.1 },
    { k: 'oi_trend_r2_min', label: 'OI trend R² min', step: 0.05 },
    { k: 'vol_sma_len', label: 'Vol SMA length', step: 1 },
    { k: 'vol_spike_mult', label: 'Vol spike ×', step: 0.1 },
    { k: 'vwap_tolerance', label: 'VWAP tolerance (frac)', step: 0.0005 },
    { k: 'vwap_slope_lookback', label: 'VWAP slope lookback', step: 1 },
    { k: 'high_vol_mult', label: 'High vol × (matrix)', step: 0.1 },
  ];
  return (
    <div className="border border-border bg-surface p-3" data-testid="oi-vwap-config-card">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] font-mono uppercase tracking-wider text-foreground flex items-center gap-1.5">
          <Settings className="w-3 h-3" /> Tuning · {symbol}
        </div>
        <label className="text-[10px] font-mono uppercase tracking-wider flex items-center gap-1.5 cursor-pointer">
          <input
            type="checkbox"
            checked={!!edit.enabled}
            onChange={(e) => setEdit({ ...edit, enabled: e.target.checked })}
            data-testid={`oi-vwap-config-enabled-${symbol}`}
          />
          {edit.enabled ? 'Enabled' : 'Disabled'}
        </label>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[10px] font-mono">
        {fields.map((f) => (
          <div key={f.k} className="flex flex-col">
            <span className="text-muted-foreground uppercase tracking-wider mb-0.5">{f.label}</span>
            <input
              type="number"
              step={f.step}
              value={edit[f.k] ?? 0}
              onChange={num(f.k)}
              className="bg-surface-highlight border border-border px-2 py-1 text-foreground"
              data-testid={`oi-vwap-config-${f.k}-${symbol}`}
            />
          </div>
        ))}
      </div>
      <div className="mt-3 flex items-center justify-end gap-2">
        {dirty && <span className="text-[10px] font-mono text-amber-300 mr-auto">Unsaved</span>}
        <button
          onClick={() => setEdit(config)}
          disabled={!dirty}
          className="px-2 py-1 text-[10px] font-mono uppercase tracking-wider border border-border hover:border-accent disabled:opacity-30"
          data-testid={`oi-vwap-config-reset-${symbol}`}
        >Reset</button>
        <button
          onClick={() => onSave(edit)}
          disabled={!dirty}
          className="px-3 py-1 text-[10px] font-mono uppercase tracking-wider border border-emerald-500/50 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20 disabled:opacity-30"
          data-testid={`oi-vwap-config-save-${symbol}`}
        >Save</button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------
// Signal table (per-minute snapshots, filtered to LONG/SHORT)
// ---------------------------------------------------------------------
const SignalsTable = ({ rows }) => {
  if (!rows || rows.length === 0) {
    return (
      <div
        className="border border-border bg-surface p-4 text-center text-xs text-muted-foreground"
        data-testid="oi-vwap-signals-empty"
      >
        No LONG/SHORT signals yet for the selected window.
      </div>
    );
  }
  return (
    <div className="border border-border bg-surface overflow-x-auto" data-testid="oi-vwap-signals-table">
      <table className="w-full text-[11px] font-mono">
        <thead className="bg-surface-highlight">
          <tr className="border-b border-border text-[10px] uppercase tracking-wider text-muted-foreground">
            <th className="px-3 py-2 text-left">Time</th>
            <th className="px-3 py-2 text-center">Signal</th>
            <th className="px-3 py-2 text-right">Entry</th>
            <th className="px-3 py-2 text-right">VWAP</th>
            <th className="px-3 py-2 text-right">OI</th>
            <th className="px-3 py-2 text-right">OI Δ%</th>
            <th className="px-3 py-2 text-right">Vol×</th>
            <th className="px-3 py-2 text-center">Sentiment</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const oiDelta = r.oi_lookback && r.oi
              ? ((r.oi - r.oi_lookback) / r.oi_lookback) * 100 : null;
            const volMult = r.vol_sma && r.vol_sma > 0 ? r.volume / r.vol_sma : null;
            return (
              <tr
                key={r.ist_minute}
                className="border-b border-border/40 hover:bg-surface-highlight"
                data-testid={`oi-vwap-signal-row-${r.ist_minute}`}
              >
                <td className="px-3 py-1.5 text-muted-foreground">
                  {r.ist_minute?.slice(11, 16)}
                </td>
                <td className="px-3 py-1.5 text-center">
                  <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] uppercase border ${
                    SIGNAL_TONE[r.signal] || SIGNAL_TONE.NONE
                  }`}>
                    {r.signal === 'LONG' && <ArrowUpRight className="w-3 h-3" />}
                    {r.signal === 'SHORT' && <ArrowDownRight className="w-3 h-3" />}
                    {r.signal}
                  </span>
                </td>
                <td className="px-3 py-1.5 text-right text-foreground">{fmt(r.close, 1)}</td>
                <td className="px-3 py-1.5 text-right text-foreground/80">{fmt(r.vwap, 1)}</td>
                <td className="px-3 py-1.5 text-right text-foreground/80">{fmtOI(r.oi)}</td>
                <td className={`px-3 py-1.5 text-right ${
                  oiDelta == null ? 'text-muted-foreground'
                    : oiDelta >= 2 ? 'text-emerald-300' : 'text-foreground'
                }`}>
                  {oiDelta == null ? '—' : (oiDelta >= 0 ? '+' : '') + oiDelta.toFixed(2) + '%'}
                </td>
                <td className={`px-3 py-1.5 text-right ${
                  volMult == null ? 'text-muted-foreground'
                    : volMult >= 1.5 ? 'text-emerald-300' : 'text-foreground'
                }`}>
                  {volMult == null ? '—' : volMult.toFixed(2) + '×'}
                </td>
                <td className="px-3 py-1.5 text-center">
                  <span className={`inline-block px-1.5 py-0.5 text-[9px] uppercase tracking-wider border ${
                    SENTIMENT_TONE[r.sentiment] || SENTIMENT_TONE.NEUTRAL
                  }`}>
                    {(r.sentiment || 'NEUTRAL').split('_')[0].toLowerCase()}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ---------------------------------------------------------------------
// Main panel
// ---------------------------------------------------------------------
const OIVwapPanel = ({ symbol = 'NIFTY' }) => {
  const [status, setStatus] = useState(null);
  const [config, setConfig] = useState(null);
  const [signals, setSignals] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [s, c, g] = await Promise.all([
        axios.get(`${API}/api/admin/oi-vwap/status?symbol=${symbol}`,
          { headers: authHeaders() }),
        axios.get(`${API}/api/admin/oi-vwap/config?symbol=${symbol}`,
          { headers: authHeaders() }),
        axios.get(`${API}/api/admin/oi-vwap/signals?symbol=${symbol}&hours=6&only_signals=true`,
          { headers: authHeaders() }),
      ]);
      setStatus(s.data);
      setConfig(c.data);
      setSignals(g.data?.rows || []);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    }
  }, [symbol]);

  useEffect(() => {
    load();
    const t = setInterval(load, 30 * 1000);
    return () => clearInterval(t);
  }, [load]);

  const onRunNow = async () => {
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/api/admin/oi-vwap/run-now?symbol=${symbol}`,
        {}, { headers: authHeaders() },
      );
      const sig = r.data?.snapshot?.signal || 'NONE';
      const sent = r.data?.snapshot?.sentiment || 'NEUTRAL';
      toast.success(`Snapshot · ${sig} · ${sent.replace(/_/g, ' ').toLowerCase()}`);
      await load();
    } catch (e) {
      toast.error(`Run now failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const onBackfill = async () => {
    if (!window.confirm(
      `Backfill last 1 day of OI-VWAP snapshots for ${symbol}?\n\n` +
      `Pulls 1-min OHLC + OI from Dhan and persists per-minute signals.`,
    )) return;
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/api/admin/oi-vwap/backfill?symbol=${symbol}&days=1`,
        {}, { headers: authHeaders() },
      );
      toast.success(`Backfill: ${r.data?.snapshots_persisted || 0} snapshots · ${r.data?.signals || 0} signals`);
      await load();
    } catch (e) {
      toast.error(`Backfill failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const [diagnose, setDiagnose] = useState(null);
  const onDiagnose = async () => {
    setBusy(true);
    setDiagnose(null);
    try {
      const r = await axios.get(
        `${API}/api/admin/oi-vwap/diagnose?symbol=${symbol}`,
        { headers: authHeaders() },
      );
      setDiagnose(r.data);
    } catch (e) {
      toast.error(`Diagnose failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const onSaveConfig = async (next) => {
    setBusy(true);
    try {
      const r = await axios.put(
        `${API}/api/admin/oi-vwap/config?symbol=${symbol}`,
        next, { headers: authHeaders() },
      );
      setConfig(r.data);
      toast.success(`${symbol} OI-VWAP tuning saved`);
    } catch (e) {
      toast.error(`Save failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const lastSnap = useMemo(() => status?.last_snapshot || null, [status]);

  return (
    <div className="space-y-3" data-testid="oi-vwap-panel">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="text-[11px] font-mono uppercase tracking-wider text-foreground flex items-center gap-1.5">
          <Target className="w-3 h-3 text-emerald-300" /> OI-VWAP Confluence · {symbol}
        </div>
        {/* Live-loop status pill */}
        {status && (
          <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider border ${
              status.market_open
                ? (status.loop_age_sec != null && status.loop_age_sec < 180)
                  ? 'border-emerald-500/60 bg-emerald-500/10 text-emerald-300'
                  : 'border-amber-500/50 bg-amber-500/10 text-amber-300'
                : 'border-border bg-surface-highlight text-muted-foreground'
            }`}
            data-testid="oi-vwap-loop-pill"
            title={
              status.market_open
                ? `Market open · last loop run ${status.loop_age_sec ?? '—'}s ago`
                : 'Market closed — loop paused (resumes 09:15 IST weekdays)'
            }
          >
            <Activity className={`w-3 h-3 ${
              status.market_open && status.loop_age_sec != null && status.loop_age_sec < 180
                ? 'animate-pulse' : ''
            }`} />
            {status.market_open
              ? (status.loop_age_sec != null && status.loop_age_sec < 180
                ? `Live · ${Math.round(status.loop_age_sec)}s ago`
                : 'Stale')
              : 'Market closed'}
          </span>
        )}
        <span className="text-[10px] font-mono text-muted-foreground">
          {status?.today_snapshots || 0} snapshots · {' '}
          <span className="text-emerald-300">{status?.today_signals || 0} signals</span> today
        </span>
        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={onRunNow}
            disabled={busy}
            className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-mono uppercase tracking-wider border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 disabled:opacity-50"
            data-testid="oi-vwap-run-now-btn"
          >
            <Zap className={`w-3 h-3 ${busy ? 'animate-spin' : ''}`} />
            Run now
          </button>
          <button
            onClick={onBackfill}
            disabled={busy}
            className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-mono uppercase tracking-wider border border-border hover:border-accent hover:text-accent disabled:opacity-50"
            data-testid="oi-vwap-backfill-btn"
          >
            <RefreshCw className={`w-3 h-3 ${busy ? 'animate-spin' : ''}`} />
            Backfill 1d
          </button>
          <button
            onClick={onDiagnose}
            disabled={busy}
            className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-mono uppercase tracking-wider border border-amber-500/40 text-amber-300 hover:bg-amber-500/10 disabled:opacity-50"
            data-testid="oi-vwap-diagnose-btn"
            title="Run end-to-end pipeline check (token / futures resolver / Dhan API / loop heartbeat)"
          >
            <AlertCircle className="w-3 h-3" />
            Diagnose
          </button>
        </div>
      </div>

      <p className="text-[10px] font-mono text-muted-foreground leading-relaxed">
        Signal fires when ALL 5 conditions align: <strong>price &gt; VWAP (Long) / &lt; VWAP (Short)</strong>,
        OI ≥ +2 % over last 15 min, OI linreg slope &gt; 0 with R² ≥ 0.5, volume &gt; 20-period SMA × 1.5,
        VWAP pullback (close within 0.1 % of VWAP, candle in trade direction), VWAP sloping in trade direction.
      </p>

      {error && (
        <div className="px-2 py-1 text-[11px] font-mono text-rose-300 border border-rose-500/40 bg-rose-500/10">
          {error}
        </div>
      )}

      {diagnose && (
        <div
          className={`border p-3 text-[11px] font-mono space-y-2 ${
            diagnose.verdict?.startsWith('OK')
              ? 'border-emerald-500/50 bg-emerald-500/10'
              : 'border-rose-500/50 bg-rose-500/10'
          }`}
          data-testid="oi-vwap-diagnose-output"
        >
          <div className="flex items-center justify-between">
            <span className={`uppercase tracking-wider ${
              diagnose.verdict?.startsWith('OK') ? 'text-emerald-300' : 'text-rose-300'
            }`}>
              {diagnose.verdict}
            </span>
            <button
              onClick={() => setDiagnose(null)}
              className="text-[10px] text-muted-foreground hover:text-foreground"
            >dismiss</button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-[10px]">
            <div>
              <div className="text-muted-foreground uppercase tracking-wider mb-1">1. Dhan token (VCP slot)</div>
              <pre className="bg-surface-highlight border border-border p-1.5 overflow-x-auto whitespace-pre-wrap">
                {JSON.stringify(diagnose.token, null, 2)}
              </pre>
            </div>
            <div>
              <div className="text-muted-foreground uppercase tracking-wider mb-1">2. Futures contract</div>
              <pre className="bg-surface-highlight border border-border p-1.5 overflow-x-auto whitespace-pre-wrap">
                {JSON.stringify(diagnose.futures, null, 2)}
              </pre>
            </div>
            <div className="sm:col-span-2">
              <div className="text-muted-foreground uppercase tracking-wider mb-1">3. Live Dhan API fetch (most important)</div>
              <pre className="bg-surface-highlight border border-border p-1.5 overflow-x-auto whitespace-pre-wrap">
                {JSON.stringify(diagnose.dhan_fetch, null, 2)}
              </pre>
            </div>
            <div className="sm:col-span-2">
              <div className="text-muted-foreground uppercase tracking-wider mb-1">4. Last persisted snapshot</div>
              <pre className="bg-surface-highlight border border-border p-1.5 overflow-x-auto whitespace-pre-wrap">
                {JSON.stringify(diagnose.last_persisted, null, 2)}
              </pre>
            </div>
          </div>
        </div>
      )}

      <LiveBanner snap={lastSnap} status={status} />
      <ConditionsRow snap={lastSnap} />

      {config && <ConfigCard symbol={symbol} config={config} onSave={onSaveConfig} />}

      <div className="text-[11px] font-mono uppercase tracking-wider text-foreground mt-2 flex items-center gap-1.5">
        <Activity className="w-3 h-3" /> LONG / SHORT signals · last 6h
      </div>
      <SignalsTable rows={signals} />

      <div
        className="border border-border bg-surface p-3 text-[10px] font-mono text-muted-foreground"
        data-testid="oi-vwap-matrix-legend"
      >
        <div className="flex items-center gap-2 mb-2 text-[11px] uppercase tracking-wider text-foreground">
          <AlertCircle className="w-3 h-3" /> Interpretation matrix
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
          <div><span className={`inline-block px-1 py-0.5 text-[9px] uppercase tracking-wider border ${SENTIMENT_TONE.STRONG_BULLISH}`}><TrendingUp className="w-2.5 h-2.5 inline mr-1" />strong bullish</span> price↑ · OI↑ · vol high</div>
          <div><span className={`inline-block px-1 py-0.5 text-[9px] uppercase tracking-wider border ${SENTIMENT_TONE.STRONG_BEARISH}`}><TrendingDown className="w-2.5 h-2.5 inline mr-1" />strong bearish</span> price↓ · OI↑ · vol high</div>
          <div><span className={`inline-block px-1 py-0.5 text-[9px] uppercase tracking-wider border ${SENTIMENT_TONE.WEAK_RALLY_BEARISH}`}>weak rally</span> price↑ · OI↓ → bearish (short-cover, no follow-through)</div>
          <div><span className={`inline-block px-1 py-0.5 text-[9px] uppercase tracking-wider border ${SENTIMENT_TONE.WEAK_SELLOFF_BULLISH}`}>weak sell-off</span> price↓ · OI↓ → bullish (long-unwind, exhaustion)</div>
        </div>
      </div>
    </div>
  );
};

export default OIVwapPanel;
